import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, Building2 } from 'lucide-react';
import { Reveal } from '@/components/Reveal';

const API = '/api';

function SkeletonCard() {
  return (
    <div className="p-10 bg-[#F7FAFC] border border-[#E5E7EB] rounded-2xl animate-pulse">
      <div className="h-3 w-24 bg-[#E5E7EB] rounded mb-5" />
      <div className="h-6 w-4/5 bg-[#E5E7EB] rounded mb-3" />
      <div className="h-6 w-3/5 bg-[#E5E7EB] rounded mb-5" />
      <div className="h-4 w-full bg-[#E5E7EB] rounded mb-2" />
      <div className="h-4 w-5/6 bg-[#E5E7EB] rounded mb-2" />
      <div className="h-4 w-4/5 bg-[#E5E7EB] rounded mb-6" />
      <div className="h-3 w-32 bg-[#E5E7EB] rounded" />
    </div>
  );
}

export default function CaseExperience() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    document.title = 'Case Experience | NKP VENTURES';
    axios.get(`${API}/cases`)
      .then(r => {
        const data = r.data;
        console.log('[CaseExperience] API response:', data);
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : null);
        if (list === null) {
          console.error('[CaseExperience] Unexpected API response shape:', data);
          setApiError(true);
        } else {
          setCases(list);
        }
      })
      .catch(err => {
        console.error('[CaseExperience] API request failed:', err);
        setApiError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* ── Hero ── */}
      <section
        className="relative pt-32 pb-24 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0A1628 0%, #153E5C 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.016) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-2.5 mb-8">
              <span className="w-5 h-px bg-[#D4AF37]" />
              <span className="overline-label text-[#D4AF37]">Case Experience</span>
            </div>
            <h1 className="font-serif font-bold text-white leading-[1.05] mb-6" style={{ fontSize: 'clamp(2.4rem, 5.5vw, 4rem)' }}>
              Operational partnerships
              <br />
              <span style={{ color: '#D4AF37' }}>built on trust.</span>
            </h1>
            <p className="text-white/55 text-lg font-sans leading-relaxed max-w-2xl">
              Our case experiences demonstrate the breadth and depth of operational support we provide — presented with the confidentiality and discretion that our clients expect and deserve.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Confidentiality commitment ── */}
      <Reveal>
        <section className="py-5 border-b border-[#D4AF37]/20" style={{ background: '#FDF8EC' }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-start gap-4">
            <Lock className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#374151] font-sans leading-relaxed">
              <span className="font-semibold text-[#153E5C]">Confidentiality Commitment: </span>
              All case experiences are published with prior client agreement and exclude any confidential operational details, internal metrics, or proprietary information. Discretion is non-negotiable.
            </p>
          </div>
        </section>
      </Reveal>

      {/* ── Cases ── */}
      <section className="py-24 lg:py-32 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#0E5A57] mb-4">Our Work</p>
            <div className="flex items-end justify-between mb-16">
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-[#153E5C] leading-tight max-w-xl">
                Engagements that<br />speak for themselves.
              </h2>
            </div>
          </Reveal>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : apiError ? (
            <div data-testid="cases-api-error" className="py-20 text-center">
              <p className="text-red-600 font-sans font-semibold mb-2">Unable to load Case Experiences.</p>
              <p className="text-[#6B7280] font-sans text-sm">Please try again later or contact support if the issue persists.</p>
              <button
                onClick={() => { setApiError(false); setLoading(true); axios.get(`${API}/cases`).then(r => { const d = r.data; setCases(Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : [])); }).catch(() => setApiError(true)).finally(() => setLoading(false)); }}
                className="mt-5 text-sm text-[#0E5A57] font-semibold hover:underline"
              >
                Retry
              </button>
            </div>
          ) : cases.length === 0 ? (
            <div data-testid="cases-empty" className="py-20 text-center">
              <p className="text-[#6B7280] font-sans">No case experiences published yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {cases.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.1}>
                  <Link
                    to={`/case-experience/${c.slug}`}
                    className="group flex flex-col h-full bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden hover:border-[#153E5C]/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    {/* Colored header bar */}
                    <div className="h-1.5 bg-gradient-to-r from-[#0E5A57] via-[#2E7D7A] to-[#D4AF37]" />

                    <div className="p-8 lg:p-10 flex flex-col flex-1">
                      {/* Client identity */}
                      <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 bg-[#153E5C] rounded-xl flex items-center justify-center flex-shrink-0">
                          <span className="font-serif font-bold text-white text-lg">
                            {c.client_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-[#153E5C] text-sm">{c.client_name}</p>
                          <p className="text-xs text-[#9CA3AF] font-sans mt-0.5">{c.industry || 'Case Experience'}</p>
                        </div>
                        <div className="ml-auto">
                          <span className="overline-label text-[#0E5A57] text-[9px]">{c.industry}</span>
                        </div>
                      </div>

                      <h2 className="font-serif text-xl lg:text-2xl font-bold text-[#153E5C] mb-5 leading-tight flex-1 group-hover:text-[#0E5A57] transition-colors duration-200">
                        {c.title}
                      </h2>

                      <p className="text-[#6B7280] text-sm font-sans leading-relaxed mb-7 line-clamp-3">
                        {c.summary}
                      </p>

                      {Array.isArray(c.services) && c.services.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-7">
                          {c.services.slice(0, 4).map(s => (
                            <span key={s} className="text-xs text-[#0E5A57] bg-teal-50 border border-teal-100 px-3 py-1 rounded-full font-sans">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2 text-sm font-semibold text-[#0E5A57] group-hover:gap-3 transition-all duration-200 mt-auto pt-6 border-t border-[#F3F4F6]">
                        Read case experience
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Approach to case studies ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="overline-label text-[#0E5A57] mb-4">Our Approach</p>
                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#153E5C] mb-6 leading-tight">
                  We let our work speak,<br />but never overshare.
                </h2>
                <p className="text-[#374151] leading-relaxed font-sans mb-6">
                  NKP VENTURES takes a deliberate approach to sharing case experiences. We present only what has been explicitly agreed with each client, and we never include metrics, internal processes, or business intelligence that could compromise confidentiality.
                </p>
                <p className="text-[#374151] leading-relaxed font-sans">
                  The trust our clients place in us is the foundation of every engagement. We guard that trust in every public communication.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Lock, title: 'Client Approved', desc: 'Published only with explicit prior agreement' },
                  { icon: Building2, title: 'No Metrics', desc: 'Internal performance data is never disclosed' },
                  { icon: ArrowRight, title: 'Context Only', desc: 'We describe nature of work, not specifics' },
                  { icon: ArrowRight, title: 'Ongoing Respect', desc: 'Confidentiality maintained after engagement' },
                ].map((item, i) => {
                  const ItemIcon = item.icon;
                  return (
                    <div key={item.title} className="p-5 bg-[#F7FAFC] border border-[#E5E7EB] rounded-xl">
                      <div className="w-8 h-8 bg-white border border-[#E5E7EB] rounded-lg flex items-center justify-center mb-3">
                        <ItemIcon className="w-4 h-4 text-[#0E5A57]" />
                      </div>
                      <p className="text-sm font-semibold text-[#153E5C] mb-1">{item.title}</p>
                      <p className="text-xs text-[#9CA3AF] font-sans leading-snug">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 bg-[#153E5C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-teal-400 mb-4">Start Your Partnership</p>
                <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                  Ready to work with<br />NKP VENTURES?
                </h2>
                <p className="text-white/60 font-sans leading-relaxed max-w-md">
                  Start a conversation about your operational needs. Every engagement begins with a no-obligation consultation.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link to="/about"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                  Learn About Us
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
